import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TIDS } from "@/constants/testIds";
import { motion } from "framer-motion";

export default function TripLandingPage() {
  const { tripKey } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/trips/${tripKey}`)
      .then((r) => setTrip(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [tripKey]);

  const flyerUrl = useMemo(() => `${API_BASE}/trips/${tripKey}/flyer?ts=${Date.now()}`, [tripKey]);

  if (err) {
    return (
      <AppLayout>
        <div className="rrdc-card p-6 text-red-600">{err}</div>
      </AppLayout>
    );
  }
  if (!trip) {
    return (
      <AppLayout>
        <div className="rrdc-card p-6 text-[#4B5563]">Loading trip details.</div>
      </AppLayout>
    );
  }

  const showDetailFallback = trip.key === "broadway"
    ? (trip.detail_show_name ? `Broadway show: ${trip.detail_show_name}` : "Broadway show details coming soon.")
    : ([trip.detail_city, trip.detail_state].filter(Boolean).join(", ")
        ? `Destination: ${[trip.detail_city, trip.detail_state].filter(Boolean).join(", ")}`
        : "Overnight destination coming soon.");

  return (
    <AppLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="text-xs uppercase tracking-widest text-[#2F50A1] font-semibold">Trip sign up</div>
        <h1 data-testid={TIDS.tripHeading} className="rrdc-heading text-3xl md:text-4xl font-bold mt-1 text-[#111827]">
          {trip.display_name}
        </h1>
        <p className="mt-2 text-[#111827] text-base font-medium no-hyphens">{showDetailFallback}</p>
        {trip.price_cents > 0 ? (
          <p className="mt-1 text-[#4B5563]">Price: <span className="font-semibold">${(trip.price_cents / 100).toFixed(2)}</span></p>
        ) : null}

        <div className="mt-6 rrdc-card p-5">
          <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold">Permission statement</div>
          <p className="mt-2 text-[#111827] leading-relaxed no-hyphens">{trip.permission_statement}</p>
        </div>

        {!trip.is_open ? (
          <div
            data-testid={TIDS.tripClosedNotice}
            className="mt-6 rrdc-card p-5 border-red-200 bg-red-50"
          >
            <div className="font-semibold text-red-800">Sign ups are currently closed for this trip.</div>
            <div className="text-sm text-red-700 mt-1">Please check back later or contact the main office.</div>
          </div>
        ) : (
          <div className="mt-6">
            <Button
              onClick={() => navigate(`/trip/${trip.key}/register`)}
              className="w-full h-14 rrdc-btn-primary rounded-2xl text-base font-semibold"
              data-testid={TIDS.tripRegisterBtn}
            >
              Register my camper
            </Button>
          </div>
        )}

        {trip.has_flyer ? (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold mb-2">Trip flyer</div>
            {trip.flyer_content_type === "application/pdf" ? (
              <a
                href={flyerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rrdc-card p-4 flex items-center justify-between hover:shadow-md transition-shadow"
                data-testid={TIDS.tripFlyer}
              >
                <span className="font-semibold text-[#2F50A1]">View flyer PDF</span>
                <span className="text-sm text-[#6B7280]">Opens in new tab</span>
              </a>
            ) : (
              <div className="rrdc-card overflow-hidden">
                <img
                  src={flyerUrl}
                  alt={`${trip.display_name} flyer`}
                  className="w-full h-auto block"
                  data-testid={TIDS.tripFlyer}
                />
              </div>
            )}
          </div>
        ) : null}

        <div className="mt-8 text-sm">
          <Link to="/" className="text-[#2F50A1] underline">Back to trips</Link>
        </div>
      </motion.div>
    </AppLayout>
  );
}
